/** Tiny typed event bus for goldChanged, bottleneckChanged, etc. */

export type GameEventMap = {
  goldChanged: { gold: number; delta: number };
  bottleneckChanged: { bottleneck: 'mining' | 'lift' | 'warehouse' | 'none' };
  saved: { at: number };
  offlineComputed: { gold: number; elapsedSeconds: number };
};

type Handler<K extends keyof GameEventMap> = (payload: GameEventMap[K]) => void;

export interface EventBus {
  on: <K extends keyof GameEventMap>(event: K, handler: Handler<K>) => () => void;
  emit: <K extends keyof GameEventMap>(event: K, payload: GameEventMap[K]) => void;
  clear: () => void;
}

export function createEventBus(): EventBus {
  const handlers = new Map<string, Set<(payload: never) => void>>();

  return {
    on(event, handler) {
      const key = event as string;
      let set = handlers.get(key);
      if (!set) {
        set = new Set();
        handlers.set(key, set);
      }
      set.add(handler as (payload: never) => void);
      return () => {
        set!.delete(handler as (payload: never) => void);
      };
    },
    emit(event, payload) {
      const set = handlers.get(event as string);
      if (!set) return;
      for (const h of set) {
        (h as (p: typeof payload) => void)(payload);
      }
    },
    clear() {
      handlers.clear();
    },
  };
}
